import type { FilesForUpdate } from './types';

type CsvFieldType = boolean | null | number | string | undefined;

const CSV_FORMULA_PREFIXES = ['=', '+', '-', '@', '\t', '\r'];

/**
 * Sanitizes a string value to prevent CSV formula injection (OWASP CSV Injection).
 * Prefixes dangerous values with a single quote so spreadsheet applications
 * treat them as text rather than formulas.
 */
export const sanitizeCsvValue = (value: string): string => {
  if (value.length > 1 && CSV_FORMULA_PREFIXES.includes(value[0])) {
    return `'${value}`;
  }

  return value;
};

export const downloadBlob = (fileName: string, blob: Blob) => {
  const anchor = document.createElement('a');
  document.body.appendChild(anchor);

  const objectUrl = window.URL.createObjectURL(blob);
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.click();
  window.URL.revokeObjectURL(objectUrl);
};

export const arrayToCsv = (data: CsvFieldType[][]) => {
  return data
    .map((row) =>
      row
        .map((value) =>
          typeof value === 'string' ? sanitizeCsvValue(value) : value
        )
        .map((value) =>
          typeof value === 'string' ? value.replaceAll('"', '""') : value
        )
        .map((value) => (typeof value === 'string' ? `"${value}"` : value))
        .map((value) => {
          if (value === undefined || value === null) {
            return '';
          }

          return String(value);
        })
        .join(',')
    )
    .join('\r\n');
};

export const humanFileSize = (bytes: number, dp = 2) => {
  const thresh = 1024;
  if (Math.abs(bytes) < thresh) {
    return bytes + ' B';
  }

  const units = ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= thresh;
    ++u;
  } while (
    Math.round(Math.abs(bytes) * r) / r >= thresh &&
    u < units.length - 1
  );

  return bytes.toFixed(dp) + ' ' + units[u];
};

/**
 * Normalises a form's `selectedFiles` into the files the user intends to keep.
 *
 * `null` / `undefined` means the field never reached the user: a conditionally hidden
 * field is nulled wholesale on submit (`FormContext`), so there is no selection to
 * read. That is not the user clearing the field and must not be treated as one —
 * returning `undefined` lets the diff helpers below preserve existing attachments.
 * An empty array is a real, explicit "remove everything" (CUS-559).
 */
export const getKeptFiles = (
  selectedFiles?: FilesForUpdate
): { Id: string | undefined }[] | undefined =>
  selectedFiles == null
    ? undefined
    : selectedFiles.filter(
        (f): f is { Id: string | undefined } =>
          f != null && !(f instanceof File)
      );

/**
 * The originals the user is no longer keeping. An `undefined` selection means the
 * field was never rendered, so nothing was deselected and nothing is removed.
 */
export const getRemovedFiles = <T extends { Id: string | undefined }>(
  originalFiles: T[],
  selectedFiles: { Id: string | undefined }[] | undefined
): T[] => {
  if (selectedFiles === undefined) {
    return [];
  }

  return originalFiles.filter(
    (originalFile) =>
      !selectedFiles.find((selectedFile) => selectedFile.Id === originalFile.Id)
  );
};

/** `getRemovedFiles`, narrowed to the ids the delete mutations take. */
export const getRemovedFilesIds = (
  originalFiles: { Id: string | undefined }[],
  selectedFiles: { Id: string | undefined }[] | undefined
): string[] =>
  getRemovedFiles(originalFiles, selectedFiles)
    .map((originalFile) => originalFile.Id)
    .filter((id): id is string => !!id);

export const hasFileChanges = (
  originalFiles?: { Id: string | undefined }[],
  selectedFiles?: FilesForUpdate
): boolean => {
  const removedFiles = getRemovedFilesIds(
    originalFiles ?? [],
    getKeptFiles(selectedFiles)
  );
  const newFiles = selectedFiles?.filter(
    (selectedFile): selectedFile is File => selectedFile instanceof File
  );

  return removedFiles.length > 0 || (newFiles ? newFiles.length > 0 : false);
};
